import { Component } from '@theme/component';
/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  static #SRCSET_WIDTHS = [400, 600, 750, 1000, 1200, 1500];
  static #SIZES = '(min-width: 768px) 752px, 100vw';
  static #SHOPIFY_DOMAINS = ['shopify.com', 'shopifycdn.com'];

  /**
   * Whether a hostname is exactly a Shopify domain or one of its subdomains.
   * Avoids the `endsWith` substring trap (e.g. `evilshopify.com`).
   * @param {string} hostname
   * @returns {boolean}
   */
  static #isShopifyHost(hostname) {
    return RTEFormatter.#SHOPIFY_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  }

  connectedCallback() {
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.querySelectorAll('img:not([srcset])').forEach(this.#makeImageResponsive);
  }

  /**
   * Adds a responsive srcset/sizes to a bare RTE image so the browser can pick
   * an appropriately sized variant instead of the full-resolution original.
   * Only Shopify-hosted images are touched, since they support the `width` transform param.
   * @param {HTMLImageElement} img
   */
  #makeImageResponsive(img) {
    const rawSrc = img.getAttribute('src');
    if (!rawSrc) return;

    let url;
    try {
      url = new URL(rawSrc, window.location.href);
    } catch {
      return;
    }

    if (!RTEFormatter.#isShopifyHost(url.hostname)) {
      return;
    }

    img.srcset = RTEFormatter.#SRCSET_WIDTHS
      .map((width) => {
        const variant = new URL(url.href);
        variant.searchParams.set('width', String(width));
        return `${variant.href} ${width}w`;
      })
      .join(', ');

    if (!img.getAttribute('sizes')) {
      img.sizes = RTEFormatter.#SIZES;
    }
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
